import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  Suspense,
} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  SECTION_RESET,
  SECTION_SIGNUP,
  SECTION_SWITCH,
  RESPONSE_CODE_TOO_MANY_REQUESTS,
  RESPONSE_CODE_VERIFICATION_FAILED,
  RECAPTCHA_SITE_KEY,
  EMPTY_RECAPTCHA,
} from './constants';
import { trackRegistration, IS_UNDER_13_COOKIE_NAME } from './util';
import NoEmailError from './NoEmailError';
import { checkEmail, localLogin } from './api';
import track from '../../services/track';
import AccountContext from '../../services/AccountContext';
import { FloatingLabelInput, Button } from 'edcom-ui';
import ReCAPTCHA from 'react-google-recaptcha';
import ErrorBoundary from './ErrorBoundary';
import RecaptchaV3, { getRecaptchaToken } from '../RecaptchaV3';

const PASSWORD_ERROR = 'That password is incorrect.';
const GENERAL_ERROR = 'There was an error signing in. Please refresh the page.';
const EMPTY_EMAIL_ERROR = 'Please enter an email address.';
const EMPTY_PASSWORD_ERROR = 'Please enter a password.';
const SERVER_SIDE_ERROR =
  "That's not working right now. Please refresh your page or try again later.";
const FacebookLogin = React.lazy(() => import('./FacebookLogin'));
const GoogleLoginEdCom = React.lazy(() => import('./GoogleLoginEdCom'));

const Login = ({
  setSection,
  defaultAccount,
  setIsLoading,
  signInDescription,
  signInTitle,
  generalError,
  setGeneralError,
  storedUsers,
  setAccount,
  setIsOpen,
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const gdprInput = useRef(null);
  const passwordInput = useRef(null);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const { refresh } = useContext(AccountContext);
  const [requireCaptcha, setRequireCaptcha] = useState(false);
  const [captchaValue, setCaptchaValue] = useState(null);
  const recaptchaRef = React.createRef();

  // default to account
  useEffect(() => {
    if (defaultAccount) {
      setEmail(defaultAccount);
      passwordInput.current.focus();
    }
  }, []);

  const loginSuccess = (gaDimensions = null) => {
    trackRegistration('login success', 'Logged In', null, gaDimensions);
    document.cookie =
      IS_UNDER_13_COOKIE_NAME +
      '=; path=/; domain=.education.com; expires=Thu, 01 Jan 1970 00:00:00 GMT';

    refresh()
      .then(() => {
        setIsLoading(false);
        setIsOpen(false);
      })
      .catch(() => {
        // Some issue refreshing account state client-side, do a full reload instead
        window.location.reload();
      });
  };

  const loginFailure = (response, code) => {
    trackRegistration('Failed to login,', 'login failure');
    if (response && response.data) {
      if (response.data.exists === false) {
        setEmailError(<NoEmailError setSection={setSection} />);
      } else if (response.data.google) {
        setPasswordError(PASSWORD_ERROR);
        setGeneralError(response.data.msg);
      } else if (response.data.gdpr) {
        setGeneralError(response.data.message);
      } else if (response.data.expiredSessionRefresh) {
        setGeneralError(response.data.message);
      } else if (code === RESPONSE_CODE_TOO_MANY_REQUESTS) {
        setRequireCaptcha(true);
      } else if (code === RESPONSE_CODE_VERIFICATION_FAILED) {
        setGeneralError(response.data.message);
      } else if (response.data.passwordError) {
        setPasswordError(PASSWORD_ERROR);
      } else if (response.data.serverSideError) {
        setGeneralError(SERVER_SIDE_ERROR);
      } else {
        setGeneralError(GENERAL_ERROR);
      }
    }
    // Error unrelated to a specific field
    else {
      setGeneralError(GENERAL_ERROR);
    }
    setIsLoading(false);
  };

  function validateEmailPresence() {
    if (email.length === 0) {
      setEmailError(EMPTY_EMAIL_ERROR);
      return false;
    }
    return true;
  }

  function validatePasswordPresence() {
    if (password.length === 0) {
      setPasswordError(EMPTY_PASSWORD_ERROR);
      return false;
    }
    return true;
  }

  function validateCaptcha() {
    if (!requireCaptcha) {
      return true;
    }
    if (captchaValue) {
      return true;
    }

    setGeneralError(EMPTY_RECAPTCHA);
    return false;
  }

  function resetRecaptcha() {
    setCaptchaValue(null);
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  }

  function updateCaptchaValue() {
    if (recaptchaRef.current) {
      setCaptchaValue(recaptchaRef.current.getValue());
    }
  }

  const login = async () => {
    if (!validateEmailPresence()) return;
    if (!validatePasswordPresence()) return;
    if (!validateCaptcha()) return;

    setIsLoading(true);
    resetRecaptcha();

    let token = '';
    try {
      // console.log('Waiting for grecaptcha to be ready');
      token = await getRecaptchaToken('login');
      console.log('Generated token');
    } catch (error) {
      console.error('reCAPTCHA execution failed', error);
      setIsLoading(false);
      return;
    }

    try {
      const json = await localLogin(
        email,
        password,
        gdprInput.current && gdprInput.current.checked ? 1 : 0,
        captchaValue || '',
        token || ''
      );
      if (json.status === '1') {
        const gaDimensions =
          'ga_dimensions' in json ? json.ga_dimensions : null;
        loginSuccess(gaDimensions);
      }
      // Failed to log in
      else {
        // Track the event only if they have 3 failed attempts to login (once)
        if ('loginAttempts' in json && json.loginAttempts === 3) {
          // Login failed 3 times; Save this data
          track('Login attempt failed 3 times');
        }
        if ('isG' in json && json.isG) {
          // google user:
          loginFailure({
            data: { status: 0, exists: true, google: true, msg: json.msg },
          });
          return;
        }
        if ('gdpr' in json && json.gdpr) {
          // user is in gdpr and has not opted in
          loginFailure({
            data: { status: 0, exists: true, gdpr: true, msg: json.msg },
          });
          return;
        }

        // Check if the username/email is valid
        const checkResponse = await checkEmail(email);
        // Username/email is for a valid account
        if (checkResponse.status) {
          loginFailure({
            data: { status: 0, exists: true, passwordError: true },
          });
        }
        // No account found
        else {
          loginFailure({ data: { status: 0, exists: false } });
        }
      }
    } catch (error) {
      if (error.response) {
        if (error.response.data) {
          loginFailure(error.response, error.code);
        } else {
          loginFailure({ data: error.response }, error.code);
        }

        return;
      }

      loginFailure();
    }
  };

  const submitForm = e => {
    e.preventDefault();
    login();
  };

  return (
    <div className="login">
      <h3>{signInTitle}</h3>
      <p className="login__description">{signInDescription}</p>

      <form className="form registration-form" onSubmit={submitForm} noValidate>
        <div className={clsx('input-row ', emailError && 'has-error')}>
          <FloatingLabelInput
            label="Email address"
            type="email"
            name="email"
            value={email}
            onChange={e => {
              setEmail(e.target.value);
              setEmailError('');
            }}
            required
          />
          <div className="errormsg">{emailError}</div>
        </div>
        <div className={clsx('input-row ', passwordError && 'has-error')}>
          <FloatingLabelInput
            label="Password"
            type="password"
            name="password"
            ref={passwordInput}
            onChange={e => {
              setPassword(e.target.value);
              setPasswordError('');
            }}
            required
          />
          <div className="errormsg">{passwordError}</div>
          <a
            className="login__forgot"
            onClick={() => {
              trackRegistration('forgot-password link clicked');
              setSection(SECTION_RESET);
            }}
          >
            <span> </span>Forgot Password?
          </a>
        </div>

        {requireCaptcha && (
          <div className="recaptcha-container">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={RECAPTCHA_SITE_KEY}
              onChange={updateCaptchaValue}
            />
            <RecaptchaV3 />
          </div>
        )}

        <Button color="primary" type="submit">
          Log In
        </Button>
        <div
          className={clsx('input-row general ', generalError && 'has-error')}
        >
          <div className="errormsg">{generalError}</div>
        </div>
        <p>
          Not an Education.com member yet?{' '}
          <a
            role="button"
            onClick={e => {
              e.preventDefault();
              trackRegistration('opened', 'Viewed Registration Form', 'signup');
              setAccount(email);
              setSection(SECTION_SIGNUP);
            }}
          >
            Create an Account
          </a>
        </p>
      </form>

      <div className="login__or ">
        <span>or</span>
      </div>
      <div className="socialButtons social__login">
        <div className="buttonWrapper">
          <ErrorBoundary
            fallback={<div className="server-warning">{SERVER_SIDE_ERROR}</div>}
          >
            <Suspense fallback={<div>Loading...</div>}>
              <FacebookLogin
                text="Sign in with Facebook"
                setIsLoading={setIsLoading}
                onSuccess={loginSuccess}
                onFailure={loginFailure}
              />
              <GoogleLoginEdCom
                setIsLoading={setIsLoading}
                onSuccess={loginSuccess}
                onFailure={loginFailure}
              />
            </Suspense>
          </ErrorBoundary>
        </div>
      </div>

      {storedUsers.length > 0 && (
        <a
          role="button"
          onClick={() => {
            trackRegistration(
              'choose account screen opened',
              'Viewed Registration Form',
              'choose-account'
            );
            setSection(SECTION_SWITCH);
          }}
        >
          {' '}
          Switch accounts{' '}
        </a>
      )}
      <div id="status" />
    </div>
  );
};

Login.propTypes = {
  storedUsers: PropTypes.array.isRequired,
  defaultAccount: PropTypes.string.isRequired,
  setSection: PropTypes.func.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  signInDescription: PropTypes.string.isRequired,
  signInTitle: PropTypes.string.isRequired,
  generalError: PropTypes.string.isRequired,
  setGeneralError: PropTypes.func.isRequired,
  setAccount: PropTypes.func.isRequired,
  setIsOpen: PropTypes.func.isRequired,
};
Login.defaultProps = {};
export default Login;
